import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, MessageCircle, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import { ChapterHeading } from "./ChapterHeading";
import { INDIVIDUAL } from "../lib/concepts";
import { useLang } from "../lib/i18n";

const LEAD_EMAIL = "info@allifyverse.com";
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "";

const inputCls = "w-full bg-transparent border border-ink/25 focus:border-gold outline-none px-5 py-4 text-sm text-ink placeholder:text-ink/35 transition-colors duration-300";

export const Contact = ({ sectors, prefill, shortlist = [] }) => {
  const [form, setForm] = useState({ name: "", email: "", sector: "", problem: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { t, tf, sectorName, lang } = useLang();

  const WA_TEXT = encodeURIComponent(
    lang === "ar" ? "مرحبًا آيات — أرغب في مناقشة مفهوم من المكتبة." : "Hello AAYAT AI — I'd like to discuss a concept from the library."
  );
  const WA_HREF = WHATSAPP_NUMBER ? `https://wa.me/${WHATSAPP_NUMBER}?text=${WA_TEXT}` : `https://wa.me/?text=${WA_TEXT}`;

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, sector: prefill.sector || f.sector, problem: prefill.problem || f.problem }));
      setSent(false);
    }
  }, [prefill]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const shortNote = shortlist.length ? `\n\nShortlisted dossiers: ${shortlist.join(", ")}` : "";
    try {
      if (WEB3FORMS_KEY) {
        const res = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            access_key: WEB3FORMS_KEY,
            subject: `AAYAT Concept Enquiry — ${form.sector}`,
            name: form.name, email: form.email, sector: form.sector, message: form.problem + shortNote
          })
        });
        if (!res.ok) throw new Error("delivery failed");
        toast.success(t("contact.toastOk"));
      } else {
        const subject = encodeURIComponent(`AAYAT Concept Enquiry — ${form.sector}`);
        const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nSector: ${form.sector}\n\n${form.problem}${shortNote}`);
        window.location.href = `mailto:${LEAD_EMAIL}?subject=${subject}&body=${body}`;
        toast.success(t("contact.toastMailto"));
      }
      setSent(true);
    } catch {
      toast.error(t("contact.toastErr") + LEAD_EMAIL);
    } finally {
      setBusy(false);
    }
  };

  const individualLabel = lang === "ar" ? "فرد / مشروع شخصي" : INDIVIDUAL;

  return (
    <section id="contact" data-testid="contact-section" className="bg-cream py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <ChapterHeading number="05" title={t("contact.title")} />
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: "easeOut" }}>
            <p className="text-ink/70 text-sm md:text-base leading-relaxed max-w-md">
              {t("contact.intro")}
            </p>
            <div className="mt-10 space-y-4 text-sm text-ink/60">
              <p><span className="text-[10px] tracking-[0.3em] uppercase text-green/60 block mb-1">{t("contact.direct")}</span>{LEAD_EMAIL}</p>
              <p><span className="text-[10px] tracking-[0.3em] uppercase text-green/60 block mb-1">{t("contact.rhythm")}</span>{t("contact.rhythmValue")}</p>
            </div>
            <a data-testid="contact-whatsapp-btn" href={WA_HREF} target="_blank" rel="noopener noreferrer"
              className="group mt-8 inline-flex items-center gap-3 border border-green/50 text-green text-xs tracking-[0.2em] uppercase px-7 py-4 hover:bg-green hover:text-cream transition-[background-color,color] duration-300">
              <MessageCircle size={15} className="transition-transform duration-300 group-hover:scale-110" />
              {t("contact.whatsapp")}
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}>
            {sent ? (
              <div data-testid="contact-success" className="border border-gold bg-gold/10 p-10 md:p-14 text-center">
                <span className="mx-auto w-12 h-12 grid place-items-center bg-gold text-ink mb-6"><Check size={20} /></span>
                <h3 className="font-display text-2xl text-ink">{tf("contact.successTitle", { name: form.name.split(" ")[0] || (lang === "ar" ? "صديقنا" : "friend") })}</h3>
                <p className="mt-4 text-sm text-ink/60 leading-relaxed max-w-sm mx-auto">
                  {t("contact.successBody")}
                </p>
                <button data-testid="contact-again-btn" onClick={() => { setSent(false); setForm({ name: "", email: "", sector: "", problem: "" }); }}
                  className="mt-8 text-xs tracking-[0.2em] uppercase text-green underline underline-offset-4 decoration-gold hover:text-ink transition-colors duration-300">
                  {t("contact.again")}
                </button>
              </div>
            ) : (
              <>
              {shortlist.length > 0 && (
                <div data-testid="contact-shortlist" className="mb-6 border border-gold/50 bg-gold/10 p-4 flex items-start gap-3">
                  <BookmarkCheck size={16} className="text-gold mt-0.5 shrink-0" />
                  <div>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-green/70 block mb-1.5">{t("contact.shortlistTitle")}</span>
                    <div className="flex flex-wrap gap-2">
                      {shortlist.map((r) => (
                        <span key={r} data-testid={`shortlist-chip-${r.toLowerCase()}`}
                          className="text-xs font-medium bg-ink text-cream px-2.5 py-1 tracking-wide">{r}</span>
                      ))}
                    </div>
                    <span className="text-[11px] text-ink/50 block mt-2">{t("contact.shortlistNote")}</span>
                  </div>
                </div>
              )}
              <form data-testid="contact-form" onSubmit={submit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className="block text-[11px] tracking-[0.3em] uppercase text-green/60 mb-3">{t("contact.name")}</label>
                    <input id="contact-name" data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder={t("contact.namePh")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-[11px] tracking-[0.3em] uppercase text-green/60 mb-3">{t("contact.email")}</label>
                    <input id="contact-email" data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder={t("contact.emailPh")} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-sector" className="block text-[11px] tracking-[0.3em] uppercase text-green/60 mb-3">{t("contact.sector")}</label>
                  <select id="contact-sector" data-testid="contact-sector" required value={form.sector} onChange={set("sector")} className={`${inputCls} cursor-pointer`}>
                    <option value="">{t("contact.choose")}</option>
                    <option value={INDIVIDUAL}>{individualLabel}</option>
                    {sectors.map((s) => <option key={s} value={s}>{sectorName(s)}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-problem" className="block text-[11px] tracking-[0.3em] uppercase text-green/60 mb-3">{t("contact.problem")}</label>
                  <textarea id="contact-problem" data-testid="contact-problem" required rows={5} value={form.problem} onChange={set("problem")}
                    placeholder={t("contact.problemPh")} className={`${inputCls} resize-none`} />
                </div>
                <button data-testid="contact-submit" type="submit" disabled={busy}
                  className="group flex items-center gap-3 bg-ink text-cream text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-green disabled:opacity-50 transition-colors duration-300">
                  {busy ? t("contact.sending") : t("contact.submit")}
                  <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
                </button>
              </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
